import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import styled from "styled-components";
import { Input, Modal, Select, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { FaUserSlash, FaCrown, FaShieldAlt, FaUserShield } from "react-icons/fa";
import { useNavContext } from "../context/nav_context";
import { api_v3_update_player_admin } from "../utils/constants";
import { Aoe2InfoPopup, ClanSelector } from "./";
import UserFormFields from "./form/UserFormFields";

const AOE2_RESOURCES = [
  { url: "https://aoe2.net/#aoe2de-profile-", icon: "aoe2net.png", alt: "AoE2 Net" },
  { url: "https://smurf.new-chapter.eu/check_player?player_id=", icon: "smurf.svg", alt: "Smurf database" },
  { url: "https://www.aoe2insights.com/user/", icon: "aoe2insights.webp", alt: "AoE2 Insights" },
  { url: "https://aoe2.gg/profile/", icon: "aoe2_gg.svg", alt: "AoE2.gg" },
];

const ROLE_CONFIG = [
  { key: "isAdmin", label: "ADMIN", color: "var(--v-mint)", Icon: FaShieldAlt },
  { key: "isModerator", label: "MOD", color: "#3860be", Icon: FaUserShield },
  { key: "isSuperAdmin", label: "SUPERADMIN", color: "#ff4d4d", Icon: FaCrown },
];

const modalStyles = {
  body: { background: "#141414", padding: "24px", color: "#fff" },
  header: { background: "#141414", borderBottom: "1px solid #303030" },
  mask: { backgroundColor: "rgba(0,0,0,0.85)" },
  content: { background: "#141414", borderRadius: "8px", boxShadow: "0 0 8px rgba(0,0,0,0.3)" },
};

const UserItem = ({ user: initialUser, clans, clansInProgress, clansError }) => {
  const { accessToken } = useNavContext();
  const [editedUser, setEditedUser] = useState({ ...initialUser });
  const [hasChanges, setHasChanges] = useState(false);
  const [saveState, setSaveState] = useState({ inProgress: false, error: false, done: false });
  const [uiState, setUiState] = useState({ openPopup: false, showClanSelector: false, showAvatarEdit: false });
  const [selectedClanId, setSelectedClanId] = useState(
    initialUser.clanId && initialUser.clanId !== "-1" ? initialUser.clanId : -1
  );
  const [newSocial, setNewSocial] = useState({ platform: "", link: "" });

  const selectedClan = useMemo(() => {
    if (!clans || !Array.isArray(clans) || selectedClanId === -1) return { name: "Ninguno", image: "" };
    const clan = clans.find((c) => c.clanId === selectedClanId);
    return clan ? { name: clan.name || "Sin nombre", image: clan.image || "" } : { name: "Ninguno", image: "" };
  }, [clans, selectedClanId]);

  useEffect(() => {
    if (selectedClanId !== (initialUser.clanId || -1)) {
      setEditedUser((prev) => ({ ...prev, clanId: selectedClanId }));
      setHasChanges(true);
    }
  }, [selectedClanId, initialUser.clanId]);

  const markChanged = (updates) => {
    setEditedUser((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    markChanged({ [name]: value });
  };

  const handleRoleToggle = (roleKey) => {
    setEditedUser((prev) => ({ ...prev, [roleKey]: !prev[roleKey] }));
    setHasChanges(true);
  };

  const handleSocialChange = (index, field, value) => {
    const updated = [...(editedUser.socialMedia || [])];
    updated[index] = { ...updated[index], [field]: value };
    markChanged({ socialMedia: updated });
  };

  const addSocialMedia = () => {
    if (!newSocial.platform || !newSocial.link) {
      message.warning("Completa plataforma y link");
      return;
    }
    const updated = [...(editedUser.socialMedia || []), { ...newSocial }];
    markChanged({ socialMedia: updated });
    setNewSocial({ platform: "", link: "" });
  };

  const removeSocialMedia = (index) => {
    const updated = (editedUser.socialMedia || []).filter((_, i) => i !== index);
    markChanged({ socialMedia: updated });
  };

  const saveUser = async () => {
    setSaveState({ inProgress: true, error: false, done: false });
    try {
      const payload = { ...editedUser, clanId: selectedClanId === -1 ? null : selectedClanId };
      const token = accessToken || localStorage.getItem("access_token");
      if (!token) { message.error("No autenticado"); return; }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(api_v3_update_player_admin(editedUser.playerId), payload, config);
      if (response.status === 200) {
        setSaveState({ inProgress: false, error: false, done: true });
        setHasChanges(false);
        setTimeout(() => setSaveState((prev) => ({ ...prev, done: false })), 2000);
        message.success("Usuario actualizado");
      }
    } catch {
      setSaveState({ inProgress: false, error: true, done: false });
      message.error("Error al guardar");
    }
  };

  return (
    <Card>
      <CardHeader>
        <AvatarWrap onClick={() => setUiState((prev) => ({ ...prev, showAvatarEdit: true }))}>
          {editedUser.discordAvatarURL ? (
            <AvatarImg src={editedUser.discordAvatarURL} alt={editedUser.discordUsername} />
          ) : (
            <AvatarPlaceholder><FaUserSlash /></AvatarPlaceholder>
          )}
        </AvatarWrap>
        <CardTitle>
          <PlayerName>{editedUser.username}</PlayerName>
          <PlayerId>ID: {editedUser.playerId}</PlayerId>
        </CardTitle>
        <RoleBadges>
          {ROLE_CONFIG.map(({ key, label, color }) =>
            editedUser[key] ? <RoleBadge key={key} $color={color}>{label}</RoleBadge> : null
          )}
        </RoleBadges>
        <ClanWrap onClick={() => setUiState((prev) => ({ ...prev, showClanSelector: true }))}>
          {selectedClan.image ? (
            <ClanImg src={selectedClan.image} alt={selectedClan.name} />
          ) : (
            <ClanPlaceholder>?</ClanPlaceholder>
          )}
          <ClanName>{selectedClan.name}</ClanName>
        </ClanWrap>
      </CardHeader>

      <MainGrid>
        <SideCol>
          <CopyBtn onClick={() => { navigator.clipboard.writeText(editedUser.playerId); message.success("ID copiado"); }}>
            COPIAR ID
          </CopyBtn>
          <CopyBtn onClick={() => { navigator.clipboard.writeText(editedUser.discordUsername); message.success("Discord copiado"); }}>
            DISCORD: {editedUser.discordUsername}
          </CopyBtn>
        </SideCol>
        <FormCol>
          <UserFormFields
            style={{ width: "100%" }}
            editedRegistration={editedUser}
            onInputChange={handleChangeForm}
            AOE2_RESOURCES={AOE2_RESOURCES}
            setOpenPopup={(open) => setUiState((prev) => ({ ...prev, openPopup: open }))}
          />
        </FormCol>
      </MainGrid>

      <Section>
        <SectionTitle>CAMPOS ADICIONALES</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>AOE2 NAME</FieldLabel>
            <TextInput name="aoe2Name" value={editedUser.aoe2Name || ""} onChange={handleChangeForm} placeholder="Nombre en AoE2" />
          </Field>
          <Field>
            <FieldLabel>AOE2 COUNTRY</FieldLabel>
            <TextInput name="aoe2Country" value={editedUser.aoe2Country || ""} onChange={handleChangeForm} placeholder="País" />
          </Field>
          <Field>
            <FieldLabel>STEAM ID</FieldLabel>
            <TextInput name="steamId" value={editedUser.steamId || ""} onChange={handleChangeForm} placeholder="Steam ID" />
          </Field>
          <Field>
            <FieldLabel>COMUNIDAD AUTÓNOMA</FieldLabel>
            <TextInput name="comunidadAutonoma" value={editedUser.comunidadAutonoma || ""} onChange={handleChangeForm} placeholder="Comunidad" />
          </Field>
          <Field>
            <FieldLabel>DISCORD USERNAME</FieldLabel>
            <TextInput name="discordUsername" value={editedUser.discordUsername || ""} onChange={handleChangeForm} placeholder="Discord" />
          </Field>
          <Field>
            <FieldLabel>DISCORD ID</FieldLabel>
            <TextInput name="discordId" value={editedUser.discordId || ""} onChange={handleChangeForm} placeholder="Discord ID" />
          </Field>
          <Field $fullWidth>
            <FieldLabel>DISCORD IDs ALTERNATIVOS</FieldLabel>
            <Select
              mode="tags"
              value={editedUser.alternateDiscordIds || []}
              onChange={(val) => markChanged({ alternateDiscordIds: val })}
              placeholder="Añade discord IDs alternativos"
              style={{ width: "100%" }}
              tokenSeparators={[",", ";", " "]}
              options={[]}
            />
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>ROLES</SectionTitle>
        <RoleRow>
          {ROLE_CONFIG.map(({ key, label, color, Icon }) => (
            <RoleToggleBtn
              key={key}
              $active={editedUser[key]}
              $color={color}
              onClick={() => handleRoleToggle(key)}
            >
              <Icon />
              <RoleLabel>{label}</RoleLabel>
            </RoleToggleBtn>
          ))}
        </RoleRow>
      </Section>

      <Section>
        <SectionTitle>REDES SOCIALES</SectionTitle>
        {editedUser.socialMedia?.map((sm, i) => (
          <SocialRow key={i}>
            <SocialInput
              placeholder="Plataforma"
              value={sm.platform}
              onChange={(e) => handleSocialChange(i, "platform", e.target.value)}
            />
            <SocialInput
              placeholder="Link"
              value={sm.link}
              onChange={(e) => handleSocialChange(i, "link", e.target.value)}
            />
            <SocialRemoveBtn onClick={() => removeSocialMedia(i)}><DeleteOutlined /></SocialRemoveBtn>
          </SocialRow>
        ))}
        <AddSocialRow>
          <SocialInput
            placeholder="Plataforma (ej: Twitter, Twitch)"
            value={newSocial.platform}
            onChange={(e) => setNewSocial((prev) => ({ ...prev, platform: e.target.value }))}
          />
          <SocialInput
            placeholder="URL"
            value={newSocial.link}
            onChange={(e) => setNewSocial((prev) => ({ ...prev, link: e.target.value }))}
          />
          <AddSocialBtn onClick={addSocialMedia}><PlusOutlined /></AddSocialBtn>
        </AddSocialRow>
      </Section>

      <ActionsRow>
        {hasChanges && (
          <SaveBtn onClick={saveUser} $loading={saveState.inProgress} $error={saveState.error} $done={saveState.done}>
            {saveState.inProgress ? "GUARDANDO..." : saveState.error ? "ERROR! REINTENTAR" : saveState.done ? "✓ GUARDADO" : "GUARDAR CAMBIOS"}
          </SaveBtn>
        )}
      </ActionsRow>

      <Modal
        open={uiState.openPopup}
        onCancel={() => setUiState((prev) => ({ ...prev, openPopup: false }))}
        footer={null}
        width="90vw"
        styles={modalStyles}
      >
        <Aoe2InfoPopup setOpenPopup={(open) => setUiState((prev) => ({ ...prev, openPopup: open }))} user={editedUser} />
      </Modal>

      <Modal
        title={`Seleccionar clan (${clans.length} clanes disponibles)`}
        open={uiState.showClanSelector}
        onCancel={() => setUiState((prev) => ({ ...prev, showClanSelector: false }))}
        footer={null}
        width={800}
        styles={modalStyles}
      >
        <ClanSelector
          isOpen
          onClose={() => setUiState((prev) => ({ ...prev, showClanSelector: false }))}
          clans={clans}
          selectedClanId={selectedClanId}
          onSelectClan={setSelectedClanId}
          onSave={() => {
            setUiState((prev) => ({ ...prev, showClanSelector: false }));
            setHasChanges(true);
          }}
          loading={clansInProgress}
          error={clansError}
        />
      </Modal>

      <Modal
        title="Editar URL del avatar"
        open={uiState.showAvatarEdit}
        onOk={() => {
          if (editedUser.discordAvatarURL) {
            setUiState((prev) => ({ ...prev, showAvatarEdit: false }));
            setHasChanges(true);
          }
        }}
        onCancel={() => setUiState((prev) => ({ ...prev, showAvatarEdit: false }))}
        okButtonProps={{ disabled: !editedUser.discordAvatarURL }}
        styles={modalStyles}
      >
        <Input
          placeholder="URL del avatar"
          value={editedUser.discordAvatarURL}
          onChange={(e) => setEditedUser((prev) => ({ ...prev, discordAvatarURL: e.target.value }))}
          style={{ backgroundColor: "#1f1f1f", borderColor: "#434343", color: "#fff" }}
        />
      </Modal>
    </Card>
  );
};

UserItem.propTypes = {
  user: PropTypes.shape({
    playerId: PropTypes.string,
    username: PropTypes.string,
    oldUserId: PropTypes.string,
    email: PropTypes.string,
    clanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comunidadAutonoma: PropTypes.string,
    aoe2Country: PropTypes.string,
    aoe2Name: PropTypes.string,
    aoe2ProfileId: PropTypes.string,
    steamId: PropTypes.string,
    discordAvatarURL: PropTypes.string,
    discordUsername: PropTypes.string,
    discordId: PropTypes.string,
    alternateDiscordIds: PropTypes.arrayOf(PropTypes.string),
    isAdmin: PropTypes.bool,
    isModerator: PropTypes.bool,
    isSuperAdmin: PropTypes.bool,
    socialMedia: PropTypes.arrayOf(PropTypes.shape({ platform: PropTypes.string, link: PropTypes.string })),
    tournamentHistory: PropTypes.array,
  }).isRequired,
  clans: PropTypes.arrayOf(PropTypes.shape({ clanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, name: PropTypes.string.isRequired, image: PropTypes.string.isRequired })).isRequired,
  clansInProgress: PropTypes.bool.isRequired,
  clansError: PropTypes.bool.isRequired,
};

const Card = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const AvatarWrap = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: var(--v-mint); }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  color: var(--v-fg-muted);
  font-size: 1.2rem;
`;

const CardTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled.div`
  font-family: var(--v-font-body);
  font-size: 18px;
  font-weight: 600;
  color: var(--v-fg);
`;

const PlayerId = styled.div`
  font-family: var(--v-font-mono);
  font-size: 10px;
  color: var(--v-fg-muted);
  margin-top: 2px;
`;

const RoleBadges = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const RoleBadge = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}18`};
  border: 1px solid ${({ $color }) => `${$color}40`};
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
`;

const ClanWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.8; }
`;

const ClanImg = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  object-fit: contain;
`;

const ClanPlaceholder = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--v-font-mono);
  font-size: 14px;
  color: var(--v-fg-muted);
`;

const ClanName = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--v-fg-muted);
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const SideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 140px;
`;

const CopyBtn = styled.button`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--v-fg-muted);
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, color 0.15s ease;
  line-height: 1.3;
  &:hover { border-color: var(--v-mint); color: var(--v-mint); }
`;

const FormCol = styled.div`
  min-width: 0;
`;

const Section = styled.div`
  padding-top: 1.25rem;
  margin-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const SectionTitle = styled.h4`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--v-fg-muted);
  margin: 0 0 0.75rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  ${({ $fullWidth }) => $fullWidth && "grid-column: 1 / -1;"}
`;

const FieldLabel = styled.label`
  display: block;
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-fg-muted);
  margin-bottom: 0.3rem;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease;
  box-sizing: border-box;
  &:focus { border-color: var(--v-mint); }
`;

const RoleRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const RoleToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 24px;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : "rgba(255,255,255,0.12)")};
  background: ${({ $active, $color }) => ($active ? `${$color}20` : "transparent")};
  color: ${({ $active, $color }) => ($active ? $color : "var(--v-fg-muted)")};
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `${$color}10`};
  }
`;

const RoleLabel = styled.span`
  letter-spacing: 0.5px;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SocialInput = styled.input`
  flex: 1;
  padding: 0.45rem 0.7rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  &:focus { border-color: var(--v-mint); }
`;

const SocialRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  background: rgba(255, 59, 59, 0.1);
  border: 1px solid rgba(255, 59, 59, 0.2);
  color: #ff4d4d;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.15s ease;
  &:hover { background: rgba(255, 59, 59, 0.2); }
`;

const AddSocialRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const AddSocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  background: rgba(60, 255, 208, 0.08);
  border: 1px dashed rgba(60, 255, 208, 0.3);
  color: var(--v-mint);
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s ease;
  &:hover { background: rgba(60, 255, 208, 0.16); border-style: solid; }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  flex-wrap: wrap;
`;

const SaveBtn = styled.button`
  background: ${({ $done }) => ($done ? "var(--v-mint)" : "var(--v-mint)")};
  border: none;
  color: #000;
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.6rem 1.5rem;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  ${({ $loading }) => $loading && "opacity: 0.6; pointer-events: none;"}
  ${({ $error }) => $error && "background: #ff3b3b; color: #fff;"}
`;

export default UserItem;
